import React, { useCallback, useEffect, useRef, useState } from 'react';
import cookies from 'js-cookie';
import { cookieNames } from '@monorepo/common';
import { useGetUserInfoQuery } from '../../services/serviceUserInfo';
import type { IUserInfo } from '../../services/serviceUserInfo/apiInterface';
import { renderShortName } from '../../utils/renderShortName';
import {
  CPG_JOBS_APP_URL,
  CPG_USER_ACCOUNT_URL,
  INSIDER_CTA_DEFAULT,
} from './cpgSites';
import {
  getJobsAccountLoginUrl,
  getJobsAccountLogoutUrl,
} from '../../utils/jobsAccountAuth';

// Bar-native auth control for the CPG cross-brand bar. Owns the whole
// `cpg-header__auth` section so it can mirror the Nombase CpgHeader: a Login link +
// "Become an Insider" CTA when anonymous, or the user's avatar + a rich account
// dropdown (name, email, subscription, Sign Out, Manage Account) when logged in
// (the Insider CTA is replaced by the account menu, matching Nombase).
//
// Auth behavior uses a mount-time cookie check, while login and logout both run
// through the shared auth portal so every auth-domain cookie is updated. The
// profile comes from the app-wide `useGetUserInfoQuery` cache (already prefetched in
// App.tsx, so there is no flash). Deliberately plain CSS (the shared `.cpg-header__*`
// classes already ported into cpg-banners.css), NOT MUI — the CpgChrome bar is
// hand-rolled and common's UserMenu/UserBadgePanel MUI panel does not fit the 38px
// bar and is unusable on touch.

interface ICpgAuthSlotProps {
  // Insider CTA href, computed by CpgBar against the live location.
  subscribeUrl: string;
  // Insider CTA button text (zeta/eta A/B, picked by CpgBar on mount).
  subscribeText?: string;
  // Called when a link/redirect is activated, so CpgBar can close its mobile dropdown.
  onNavigate?: () => void;
}

// Avatar for the logged-in user; person-in-circle icon when there is no headshot.
function UserAvatar({ user, size, className }: { user: IUserInfo; size: number; className?: string }) {
  if (user.headshot) {
    return (
      <img className={className} src={user.headshot} alt={user.firstName || user.name || 'User avatar'} width={size} height={size} />
    );
  }
  return (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        fill="currentColor"
        d="M12 2c5.52 0 10 4.48 10 10s-4.48 10-10 10S2 17.52 2 12 6.48 2 12 2ZM6.023 15.416C7.491 17.606 9.695 19 12.16 19c2.464 0 4.669-1.393 6.136-3.584A8.968 8.968 0 0 0 12.16 13a8.968 8.968 0 0 0-6.137 2.416ZM12 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z"
      />
    </svg>
  );
}

// Membership line under the name — mirrors Nombase's "Subscription:" row.
function subscriptionLabel(user: IUserInfo): string {
  if (user.isCharterMember) return 'Charter Member';
  if (user.subscriptionTypes && user.subscriptionTypes.length > 0) return user.subscriptionTypes[0];
  if (user.isSubscriber) return 'Insider';
  return '';
}

function UserInfo({
  user,
  onLogout,
  onLinkClick,
}: {
  user: IUserInfo;
  onLogout: () => void;
  onLinkClick: () => void;
}) {
  const sub = subscriptionLabel(user);
  return (
    <>
      <div className="cpg-header__user-info-intro">
        <div className="cpg-header__user-info-intro-img">
          {user.headshot ? <UserAvatar user={user} size={34} /> : null}
        </div>
        <div className="cpg-header__user-info-intro-text">
          <p className="cpg-header__user-info-intro-name" title={user.name}>
            {user.name}
          </p>
          <p className="cpg-header__user-info-intro-email" title={user.email}>
            {user.email}
          </p>
        </div>
      </div>
      {sub ? (
        <p className="cpg-header__user-info-sub cpg-header__user-info-sub--visible">
          <strong>Subscription:</strong> {sub}
        </p>
      ) : null}
      <nav className="cpg-header__account-links" aria-label="CPG Jobs account links">
        {[
          ['My Applications', '/user/job-seeker/applications'],
          ['Saved Jobs', '/user/job-seeker/saved'],
          ['My Resume', '/user/job-seeker/resume'],
          ['Manage Job Listings', '/user/employer/listings'],
          ['Applicants', '/user/employer/applicants'],
        ].map(([label, path]) => (
          <a
            key={path}
            className="cpg-header__manage-jobs"
            href={`${CPG_JOBS_APP_URL}${path}`}
            onClick={onLinkClick}
          >
            {label}
          </a>
        ))}
      </nav>
      <div className="cpg-header__user-info-actions">
        {/* Logout starts the shared server-side sign-out chain. */}
        <button
          type="button"
          className="cpg-header__nav-item cpg-header__nav-item--link"
          onClick={() => {
            onLinkClick();
            onLogout();
          }}
        >
          Sign Out
        </button>
        <a
          className="cpg-header__btn"
          href={CPG_USER_ACCOUNT_URL}
          target="_blank"
          rel="noopener noreferrer nofollow"
          onClick={onLinkClick}
        >
          Manage Account
        </a>
      </div>
    </>
  );
}

export const CpgAuthSlot = ({
  subscribeUrl,
  subscribeText = INSIDER_CTA_DEFAULT.text,
  onNavigate,
}: ICpgAuthSlotProps) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showUserInfo, setShowUserInfo] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  // Mount-time auth check — identical to common UserMenu (cookie presence).
  useEffect(() => {
    setIsLoggedIn(Boolean(cookies.get(cookieNames.bevnet_token)));
  }, []);

  // Profile from the app's existing user-info cache; skip while anonymous so we
  // don't fire a needless request.
  const { data } = useGetUserInfoQuery(undefined, { skip: !isLoggedIn });
  const authed = isLoggedIn && Boolean(data?.isLoggedIn);

  // Click-toggled dropdown: outside-click + Escape close (same pattern as CpgBar's
  // Brands dropdown).
  useEffect(() => {
    if (!showUserInfo) return undefined;
    const onMouseDown = (e: MouseEvent) => {
      const node = wrapperRef.current;
      if (node && e.target instanceof Node && !node.contains(e.target)) {
        setShowUserInfo(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowUserInfo(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [showUserInfo]);

  const redirect = useCallback(() => {
    onNavigate?.();
    window.location.assign(getJobsAccountLoginUrl(window.location.href));
  }, [onNavigate]);

  const logOut = useCallback(() => {
    window.location.assign(getJobsAccountLogoutUrl(window.location.href));
  }, []);

  const closeUserInfo = useCallback(() => setShowUserInfo(false), []);

  if (!authed || !data) {
    // Anonymous (or profile not yet confirmed): Login link + Insider CTA.
    return (
      <section className="cpg-header__auth">
        <a
          className="cpg-header__nav-item cpg-header__nav-item--link"
          href={getAnonLoginHref()}
          onClick={(e) => {
            e.preventDefault();
            redirect();
          }}
        >
          Login
        </a>
        <a
          className="cpg-header__btn cpg-header__btn--subscribe"
          role="button"
          href={subscribeUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onNavigate}
        >
          {subscribeText}
        </a>
      </section>
    );
  }

  return (
    <section className="cpg-header__auth cpg-header__auth--logged-in" ref={wrapperRef}>
      <div className="cpg-header__user-info-wrapper">
        <button
          type="button"
          className="cpg-header__nav-item cpg-header__nav-item--btn"
          aria-haspopup="true"
          aria-expanded={showUserInfo}
          aria-controls="cpg-header-user-info-dropdown"
          title="User Profile Menu"
          onClick={() => setShowUserInfo((v) => !v)}
        >
          <span className="cpg-header__user-avatar">
            <UserAvatar user={data} size={24} className="cpg-header__user-avatar-img" />
          </span>
          {/* "First L." — the bevnet.com header format (renderShortName needs the
              full name; firstName alone would just render the first name back). */}
          <span className="cpg-header__user-name" aria-live="polite">
            {renderShortName(data.name) || data.firstName}
          </span>
        </button>
        <div
          id="cpg-header-user-info-dropdown"
          className={
            'cpg-header__user-info-dropdown' + (showUserInfo ? ' cpg-header__user-info-dropdown--open' : '')
          }
          aria-hidden={showUserInfo ? 'false' : 'true'}
        >
          <UserInfo user={data} onLogout={logOut} onLinkClick={closeUserInfo} />
        </div>
      </div>
      <div className="cpg-header__auth-mobile">
        <UserInfo user={data} onLogout={logOut} onLinkClick={closeUserInfo} />
      </div>
    </section>
  );
};

// Real, navigable href for the anon Login link (progressive enhancement); the
// onClick recomputes it against the live location and closes the mobile dropdown.
function getAnonLoginHref(): string {
  const returnUrl = typeof window !== 'undefined' ? window.location.href : '';
  return getJobsAccountLoginUrl(returnUrl);
}

export default CpgAuthSlot;
